<template>
    <div class="p-2">
        <div
            :class="[
                'w-full h-full rounded-card',
                backgroundClasses,
                {
                    'divide-border after:bg-border': dividerType == 'full',
                },
            ]"
            :style="dividerLeftPosition"
        >
            <div v-if="!loading" class="flex flex-col flex-shrink-0 relative">
                <div
                    :class="{
                        'image-container after:bg-border ':
                            dividerType == 'image',
                    }"
                >
                    <div
                        class="absolute top-2 right-2 z-[1]"
                        v-if="
                            showOptionsButton && options && options.length > 0
                        "
                    >
                        <ButtonPopoverMenu :items="options">
                            <template #button="{ open }">
                                <div
                                    :class="[
                                        open
                                            ? `border-${item.modelClass}-accent bg-${item.modelClass}-accent text-${item.modelClass}`
                                            : 'border-transparent',
                                        `bg-${item.modelClass}-accent hover:bg-${item.modelClass}-accent-hover text-${item.modelClass}`,
                                        'size-10 p-2 rounded-full transition duration-button-hover focus-style hover:scale-95',
                                    ]"
                                >
                                    <PhDotsThreeOutline
                                        class="flex-grow"
                                        weight="fill"
                                    />
                                </div>
                            </template>
                        </ButtonPopoverMenu>
                    </div>

                    <DynamicLink :href="item.url" class="relative block">
                        <ModelImage
                            :src="
                                item.modelClass == 'user' ||
                                item.modelClass == 'organization'
                                    ? item.coverImageUrl
                                    : item.imageUrl
                            "
                            :modelClass="item.modelClass"
                            :useGradient="useGradientPlaceholder"
                            :clickable="true"
                            imageClasses="object-cover w-full h-full"
                            :class="[
                                aspectRatio,
                                'w-full rounded-t-img cursor-pointer',
                            ]"
                            :typeId="item.type_id"
                            :fileUrl="item.file"
                            :hideIcon="
                                !!(
                                    (item.modelClass == 'user' ||
                                        item.modelClass == 'organization') &&
                                    item.imageUrl
                                )
                            "
                        />
                        <div
                            class="absolute top-2 left-2 z-[1] flex flex-col gap-1"
                        >
                            <DraftTag
                                v-if="item.is_draft"
                                :modelClass="item.modelClass"
                            />
                            <FeaturedTag
                                v-if="item.is_featured"
                                :modelClass="item.modelClass"
                            />
                        </div>

                        <div
                            v-if="
                                (item.modelClass == 'user' ||
                                    item.modelClass == 'organization') &&
                                item.imageUrl
                            "
                            :class="[
                                {
                                    'rounded-full': item.modelClass == 'user',
                                },
                                {
                                    'rounded-img':
                                        item.modelClass == 'organization',
                                },
                                'absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2',
                            ]"
                        >
                            <ModelImage
                                :src="item.imageUrl"
                                :modelClass="item.modelClass"
                                :clickable="true"
                                :useGradient="useGradientPlaceholder"
                                :fallbackBorder="`border border-${item.modelClass}-accent`"
                                :class="[
                                    {
                                        'rounded-full':
                                            item.modelClass == 'user',
                                    },
                                    {
                                        'rounded-img':
                                            item.modelClass == 'organization',
                                    },
                                    `border border-${item.modelClass}-accent`,
                                ]"
                                heightClasses="h-28 aspect-square"
                                classes="flex items-center bg-white"
                                :imageClasses="[
                                    { 'size-28': item.modelClass == 'user' },
                                ]"
                                objectFitClasses="object-center object-cover"
                            />
                        </div>
                    </DynamicLink>
                </div>
                <div class="flex flex-col gap-y-[2px] p-4">
                    <!-- Date & tags -->
                    <div
                        class="flex flex-wrap items-center gap-x-2 gap-y-1 text-text-secondary text-xs uppercase leading-none"
                    >
                        <p
                            v-if="item.date"
                            class="py-[5px] flex-shrink-0"
                            :class="{
                                'text-draft-content-text':
                                    item.is_draft && !item.is_featured,
                            }"
                            v-text="$moment(item.date).format('L')"
                        />
                        <ModelStyledTag
                            v-if="item.type"
                            :text="item.type"
                            :small="true"
                            :modelClass="item.modelClass"
                        />
                        <component
                            v-if="item.tagComponent"
                            :is="item.tagComponent"
                            v-bind="item.tagComponentProps || {}"
                        />
                    </div>
                    <!-- Title -->
                    <TruncateTooltip>
                        <DynamicLink
                            :class="[
                                'w-fit max-w-full font-semibold underline-hover line-clamp-2 font-header',
                                {
                                    'text-base': smallTitle,
                                },
                                {
                                    'text-lg': !smallTitle,
                                },
                                {
                                    'text-draft-content-text':
                                        item.is_draft && !item.is_featured,
                                },
                            ]"
                            :href="item.url"
                        >
                            <p v-text="item.name"></p
                        ></DynamicLink>
                    </TruncateTooltip>
                    <!-- SubtitleLink -->
                    <TruncateTooltip>
                        <Link
                            v-if="item.subtitleLink"
                            :href="item.subtitleLink.url"
                            v-text="item.subtitleLink.text"
                            class="block w-fit max-w-full text-sm py-0.5 text-text-secondary underline-hover truncate"
                        />
                    </TruncateTooltip>
                    <!-- Description -->
                    <p
                        v-if="showDescription"
                        class="margin-t-0.5 text-sm line-clamp-3"
                        :class="{
                            'text-draft-content-text':
                                item.is_draft && !item.is_featured,
                        }"
                        v-text="item.short_description || item.description"
                    />
                </div>
            </div>
            <div v-else class="animate-pulse space-y-2">
                <div class="w-full aspect-video bg-gray-50 rounded-img" />
                <div class="w-1/2 h-4 bg-gray-50 rounded-sm" />
                <div class="w-full h-6 bg-gray-50 rounded-sm" />
                <div class="w-full h-12 bg-gray-50 rounded-sm" />
            </div>
        </div>
        <ConfirmDeleteBaseModelModal
            v-if="showOptionsButton && item.can?.delete"
            ref="confirmDeleteModal"
            :modelClass="item.modelClass"
            :modelId="item.id"
            @deleted="$emit('deleted')"
        />
    </div>
</template>

<script>
import ModelImage from '@/Components/ModelComponents/ModelImage.vue'
import DraftTag from '@/Components/Elements/Tags/DraftTag.vue'
import FeaturedTag from '@/Components/Elements/Tags/FeaturedTag.vue'
import TruncateTooltip from '@/Components/Utilities/TruncateTooltip.vue'
import TypeTag from '@/Components/Elements/Tags/TypeTag.vue'
import ModelStyledTag from '@/Components/Elements/Tags/ModelStyledTag.vue'
import { Link } from '@inertiajs/vue3'
import DynamicLink from '@/Components/Utilities/DynamicLink.vue'
import ButtonPopoverMenu from '@/Components/Utilities/ButtonPopoverMenu.vue'
import { PhDotsThreeOutline } from '@phosphor-icons/vue'
import ConfirmDeleteBaseModelModal from '@/Components/Modals/ConfirmDeleteBaseModelModal.vue'

export default {
    name: 'GridCard',

    components: {
        ModelImage,
        TruncateTooltip,
        Link,
        DynamicLink,
        ButtonPopoverMenu,
        ConfirmDeleteBaseModelModal,
        PhDotsThreeOutline,
        TypeTag,
        ModelStyledTag,
        DraftTag,
        FeaturedTag,
    },

    emits: ['deleted'],

    props: {
        item: {
            type: Object,
            required: true,
        },
        dividerType: {
            type: [String, null],
            validatior: (value) =>
                value == null || ['full', 'image'].includes(value),
        },
        aspectRatio: {
            type: String,
            default: 'aspect-video',
        },
        xGapSize: {
            type: String,
            required: true,
            validator: (value) => {
                if (!value.includes('px]')) {
                    console.error(
                        'xGapSize must be a tailwind class with a pixel value like this gap-x-[17px]. This is to ensure that we can calculate the position of the border correctly.'
                    )
                    return false
                }
                return true
            },
        },
        loading: {
            type: Boolean,
            default: false,
        },
        showDescription: {
            type: Boolean,
            default: true,
        },
        smallTitle: {
            type: Boolean,
            default: false,
        },
        showOptionsButton: {
            type: Boolean,
            default: true,
        },
        useModelBackgroundOnFeatured: {
            type: Boolean,
            default: false,
        },
    },

    created() {
        if (this.showOptionsButton) {
            this.options = []
            if (this.item.can?.edit) {
                this.options.push({
                    text: this.$lang.get('Edit'),
                    href: this.$localizedRoute(
                        `${this.item.modelClass}.edit`,
                        this.item.id
                    ),
                    type: 'link',
                })
            }
            if (this.item.can?.delete) {
                this.options.push({
                    text: this.$lang.get('Delete'),
                    type: 'button',
                    action: () => {
                        this.$refs.confirmDeleteModal.confirmDelete()
                    },
                })
            }
        }
    },

    computed: {
        /**
         * Extract the pixel value from the gap size and return
         * the left position for the divider.
         */
        dividerLeftPosition() {
            if (this.xGapSize) {
                let gapWidth = parseInt(this.xGapSize.match(/\d+/)[0])
                gapWidth = Math.ceil(gapWidth / 2)
                // Add the padding of the card to the gap width
                gapWidth = gapWidth + 8

                return {
                    '--divider-left-position': -gapWidth + 'px',
                }
            }
        },

        useGradientPlaceholder() {
            return this.$page?.props?.useGradientPlaceholder ?? false
        },

        backgroundClasses() {
            if (this.useModelBackgroundOnFeatured && this.item.is_featured) {
                return `bg-${this.item.modelClass}`
            } else if (this.item.is_draft) {
                return 'bg-draft'
            }
            return 'bg-transparent'
        },
    },
}
</script>
<style scoped>
.image-container,
.divide-border {
    position: relative;
}

.image-container:after {
    content: '';
    height: 100%;
    width: 1px;

    position: absolute;
    left: var(--divider-left-position);
    top: 0;
}

.divide-border:after {
    content: '';
    height: 98%;
    width: 1px;
    position: absolute;
    left: var(--divider-left-position);
    top: 0;
}
</style>
